import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import type { LoopDto, ProjectKind } from '@aiai/shared';
import { deleteLoop, fetchProject, fetchProjectLoops } from '../api';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { loopEditHref, loopsHref, projectNav } from '../lib/projectNav';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { snippet, formatRelative } from '../lib/format';
import { cronLabel } from '../lib/cronPresets';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ProjectSectionNav } from '../components/ProjectSectionNav';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { OverflowMenu, type OverflowMenuItem } from '../components/OverflowMenu';
import { PageHeader } from '../components/PageHeader';
import { Row, RowList } from '../components/Row';
import { LoopStateBadge } from '../components/StatusBadge';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLoopControls } from '../hooks/useLoopControls';
import { useCapabilities } from '../hooks/useCapabilities';
import { useFeature } from '../hooks/useEntitlements';
import { FeatureLocked } from '../components/FeatureLocked';

function LoopRow({
  loop,
  projectId,
  projectKind,
  canManage,
  loopsOn,
  onRequestDelete,
}: {
  loop: LoopDto;
  projectId: string;
  projectKind: ProjectKind;
  canManage: boolean;
  /** False while the operator has switched loops off: only Delete stays (it's allowed while off). */
  loopsOn: boolean;
  onRequestDelete: (loop: LoopDto) => void;
}) {
  const navigate = useNavigate();
  const { wp } = useWorkspacePath();
  const { fireButton, toggle, feedback, fire, firePending } = useLoopControls(loop, projectKind);

  const editHref = wp(loopEditHref(projectKind, projectId, loop.id));
  const description = snippet(loop.description) || cronLabel(loop.cronExpression);
  // While loops are off nothing fires, so a "Next …" time would promise a skipped fire.
  const timestamp =
    loopsOn && loop.nextRunAt
      ? `Next ${formatRelative(loop.nextRunAt)}`
      : loop.lastFiredAt
        ? `Last fired ${formatRelative(loop.lastFiredAt)}`
        : undefined;

  const deleteItem: OverflowMenuItem = {
    label: 'Delete',
    icon: <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />,
    onClick: () => onRequestDelete(loop),
    danger: true,
  };
  // Fire, toggle and edit all 403 while loops are off; only Delete is offered then.
  const menuItems: OverflowMenuItem[] = loopsOn
    ? [
        {
          label: 'Fire now',
          icon: <Play className="h-3.5 w-3.5" aria-hidden="true" />,
          onClick: fire,
          disabled: firePending || !loop.enabled,
        },
        {
          label: 'Edit',
          icon: <Pencil className="h-3.5 w-3.5" aria-hidden="true" />,
          onClick: () => navigate(editHref),
        },
        deleteItem,
      ]
    : [deleteItem];

  // Members can view loops but not fire/toggle/edit/delete them (owner-only).
  return (
    <>
      <Row
        to={loopsOn ? editHref : undefined}
        badge={<LoopStateBadge enabled={loop.enabled} size="sm" />}
        title={loop.name}
        description={description}
        timestamp={timestamp}
        actions={
          canManage ? (
            <div className="flex items-center gap-1">
              {loopsOn && fireButton}
              {loopsOn && toggle}
              <OverflowMenu items={menuItems} label="Loop actions" size="sm" />
            </div>
          ) : undefined
        }
      />
      {canManage && loopsOn && feedback}
    </>
  );
}

export function LoopsPage() {
  const { projectId = '' } = useParams();
  const { wp } = useWorkspacePath();
  const { can } = useCapabilities();
  const canManage = can('loop:manage');
  const loopsOn = useFeature('loops');
  const canCreate = canManage && loopsOn !== false;
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<LoopDto | null>(null);

  const projectQuery = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: !!projectId,
  });

  // Keep the URL on the pretty code; fetch loops by the resolved UUID.
  useCanonicalProjectRef(projectId, projectQuery.data?.code);
  const resolvedId = projectQuery.data?.id;

  const loopsQuery = useQuery({
    queryKey: qk.projectLoops(resolvedId ?? projectId),
    queryFn: () => fetchProjectLoops(resolvedId!),
    enabled: !!resolvedId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteLoop(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.projectLoops(resolvedId ?? projectId) });
      setDeleteTarget(null);
    },
  });

  useDocumentTitle(
    projectQuery.data ? `Loops · ${projectQuery.data.name}` : 'Loops',
  );

  if (projectQuery.isLoading || loopsQuery.isLoading) {
    return <LoadingState label="Loading loops…" />;
  }

  if (projectQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(projectQuery.error, 'Failed to load project')}
        onRetry={() => projectQuery.refetch()}
      />
    );
  }

  const project = projectQuery.data!;
  const loops = loopsQuery.data ?? [];
  const nav = projectNav(project);

  return (
    <div className="space-y-6 pb-16 sm:pb-0">
      <Breadcrumbs
        items={[
          { label: nav.rootLabel, to: wp(nav.rootTo) },
          { label: nav.name, to: wp(nav.selfTo) },
          { label: 'Loops' },
        ]}
      />

      <PageHeader
        title="Scheduled loops"
        subtitle="Recurring agent tasks that fire on a cron schedule."
        actions={
          canCreate ? (
            <Link to={wp(`${loopsHref(project.kind, projectId)}/new`)} className="btn-primary">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New loop
            </Link>
          ) : undefined
        }
      />

      <ProjectSectionNav
        kind={project.kind}
        projectRef={project.code ?? project.id}
        active="loops"
      />

      {loopsOn === false && <FeatureLocked feature="loops" />}

      {loopsQuery.isError && (
        <ErrorState
          message={errorMessage(loopsQuery.error, 'Failed to load loops')}
          onRetry={() => loopsQuery.refetch()}
        />
      )}

      {loops.length === 0 && !loopsQuery.isError ? (
        <EmptyState
          icon={<Clock className="h-8 w-8" aria-hidden="true" />}
          title="No scheduled loops yet"
          description={
            canCreate
              ? 'Create a loop to run an agent automatically on a cron schedule — like a weekly security audit or a daily product critic.'
              : 'No scheduled loops yet. A workspace owner can set one up.'
          }
          action={
            canCreate ? (
              <Link to={wp(`${loopsHref(project.kind, projectId)}/new`)} className="btn-primary">
                <Plus className="h-4 w-4" aria-hidden="true" />
                New loop
              </Link>
            ) : undefined
          }
        />
      ) : (
        <RowList>
          {loops.map((loop) => (
            <LoopRow
              key={loop.id}
              loop={loop}
              projectId={projectId}
              projectKind={project.kind}
              canManage={canManage}
              loopsOn={loopsOn !== false}
              onRequestDelete={setDeleteTarget}
            />
          ))}
        </RowList>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete loop?"
        confirmLabel="Delete loop"
        busy={deleteMutation.isPending}
        error={
          deleteMutation.isError
            ? errorMessage(deleteMutation.error, 'Could not delete loop')
            : null
        }
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        onClose={() => {
          deleteMutation.reset();
          setDeleteTarget(null);
        }}
        message={
          <>
            Delete{' '}
            <span className="font-medium text-ink">
              {deleteTarget?.name}
            </span>
            ? Past issues and runs created by this loop will be preserved.
          </>
        }
      />

    </div>
  );
}
